import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  ScatterController,
  CategoryScale,
} from "chart.js";
import { getRelativePosition } from "chart.js/helpers";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
  ScatterController
);

const isFiniteNum = (v) => Number.isFinite(Number(v));

const WORD_LANE = {
  heightDesktop: 34,
  heightMobile: 30,
  gapFromPlot: 8,
  fontDesktop: 15,
  fontMobile: 14,
  padXDesktop: 14,
  padXMobile: 12,
  minChipDesktop: 34,
  minChipMobile: 28,
  radius: 999,
};

function clamp(v, a, b) {
  return Math.min(Math.max(v, a), b);
}

function roundRect(ctx, x, y, w, h, r) {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

function buildContinuousGuideCurve(xs, ys) {
  if (!xs?.length || !ys?.length) return [];

  const out = new Array(ys.length).fill(null);

  for (let i = 0; i < ys.length; i++) {
    const y = ys[i];
    out[i] = y != null && Number.isFinite(Number(y)) ? Number(y) : null;
  }

  let firstValid = -1;
  for (let i = 0; i < out.length; i++) {
    if (out[i] != null) {
      firstValid = i;
      break;
    }
  }
  if (firstValid === -1) return [];

  for (let i = 0; i < firstValid; i++) out[i] = out[firstValid];

  let lastValid = -1;
  for (let i = out.length - 1; i >= 0; i--) {
    if (out[i] != null) {
      lastValid = i;
      break;
    }
  }
  for (let i = lastValid + 1; i < out.length; i++) out[i] = out[lastValid];

  let i = firstValid;
  while (i < out.length) {
    if (out[i] != null) {
      i++;
      continue;
    }

    const start = i - 1;
    let end = i;
    while (end < out.length && out[end] == null) end++;
    if (end >= out.length) break;

    const x1 = Number(xs[start]);
    const x2 = Number(xs[end]);
    const y1 = out[start];
    const y2 = out[end];
    const span = x2 - x1 || 1;

    for (let k = i; k < end; k++) {
      const x = Number(xs[k]);
      const t = (x - x1) / span;
      out[k] = y1 + (y2 - y1) * t;
    }

    i = end;
  }

  const smoothPass = (arr) => {
    const next = [...arr];
    for (let j = 1; j < arr.length - 1; j++) {
      next[j] = (arr[j - 1] + arr[j] + arr[j + 1]) / 3;
    }
    return next;
  };

  let smoothed = out.map((v) => Number(v));
  smoothed = smoothPass(smoothed);
  smoothed = smoothPass(smoothed);

  return xs.map((x, idx) => ({
    x: Number(x),
    y: smoothed[idx],
  }));
}

function interpolateOnCurve(points, xTarget) {
  if (!points?.length || !Number.isFinite(Number(xTarget))) return null;
  if (points.length === 1) return { x: xTarget, y: points[0].y };

  if (xTarget <= points[0].x) return { x: xTarget, y: points[0].y };
  if (xTarget >= points[points.length - 1].x) {
    return { x: xTarget, y: points[points.length - 1].y };
  }

  for (let i = 0; i < points.length - 1; i++) {
    const p1 = points[i];
    const p2 = points[i + 1];

    const minX = Math.min(p1.x, p2.x);
    const maxX = Math.max(p1.x, p2.x);

    if (xTarget >= minX && xTarget <= maxX) {
      const span = p2.x - p1.x || 1;
      const t = (xTarget - p1.x) / span;
      return {
        x: xTarget,
        y: p1.y + (p2.y - p1.y) * t,
      };
    }
  }

  return { x: xTarget, y: points[points.length - 1].y };
}

function getRenderedWordChipBounds({
  word,
  xScale,
  chartArea,
  isMobile,
  clipMin,
  clipMax,
}) {
  const startC = clipMin == null ? word.start : Math.max(word.start, clipMin);
  const endC = clipMax == null ? word.end : Math.min(word.end, clipMax);
  if (!(endC > startC)) return null;

  const x1 = xScale.getPixelForValue(startC);
  const x2 = xScale.getPixelForValue(endC);

  const segLeft = Math.min(x1, x2);
  const segW = Math.max(1, Math.abs(x2 - x1));
  const segCenter = segLeft + segW / 2;

  const minChipW = isMobile
    ? WORD_LANE.minChipMobile
    : WORD_LANE.minChipDesktop;

  const drawW = Math.max(minChipW, segW);
  let chipX = segLeft;

  if (segW < minChipW) chipX = segCenter - drawW / 2;

  const inset = 2;
  chipX = clamp(
    chipX,
    chartArea.left + inset,
    chartArea.right - inset - drawW
  );

  return {
    left: chipX,
    right: chipX + drawW,
    center: chipX + drawW / 2,
  };
}

function makeLanePlugin(getLanePx) {
  return {
    id: "lanePlugin",

    beforeLayout(chart, _args, opts) {
      const showWordLane = opts?.showWordLane ?? true;
      const lanePx = showWordLane ? getLanePx(!!opts?.isMobile) : 0;
      const x = chart.scales?.x;
      if (!x) return;

      const prevAfterFit = x.options.afterFit;

      x.options.afterFit = (scale) => {
        if (typeof prevAfterFit === "function") prevAfterFit(scale);

        if (scale.$laneBaseHeight == null) {
          scale.$laneBaseHeight = scale.height || 0;
        }
        scale.height = scale.$laneBaseHeight + lanePx;
      };
    },

    afterDatasetsDraw(chart, _args, pluginOptions) {
      const {
        wordIntervals = [],
        activeWord = null,
        hoveredWord = null,
        cursorX = null,
        cursorEnvelopePoint = null,
        ballRadius = null,
        showBall = true,
        showWordLabels = true,
        showWordLane = true,
        isMobile = false,
        xWindowMin = null,
        xWindowMax = null,
      } = pluginOptions || {};

      const { ctx, chartArea, scales } = chart;
      const xScale = scales.x;
      const yScale = scales.y;
      if (!ctx || !chartArea || !xScale || !yScale) return;

      const { top, bottom, left, right } = chartArea;

      const laneH = isMobile ? WORD_LANE.heightMobile : WORD_LANE.heightDesktop;
      const gap = WORD_LANE.gapFromPlot;
      const laneTop = bottom - laneH;
      const effectiveLaneBottom = bottom;

      const fontSize = isMobile ? WORD_LANE.fontMobile : WORD_LANE.fontDesktop;
      const padX = isMobile ? WORD_LANE.padXMobile : WORD_LANE.padXDesktop;
      const minChipW = isMobile
        ? WORD_LANE.minChipMobile
        : WORD_LANE.minChipDesktop;

      const clipMin =
        Number.isFinite(Number(xWindowMin)) ? Number(xWindowMin) : null;
      const clipMax =
        Number.isFinite(Number(xWindowMax)) ? Number(xWindowMax) : null;

      ctx.save();

      if (showWordLane) {
        ctx.beginPath();
        ctx.rect(left, top, right - left, bottom - top);
        ctx.clip();

        ctx.fillStyle = "rgba(15,23,42,0.035)";
        ctx.strokeStyle = "rgba(148,163,184,0.30)";
        ctx.lineWidth = 1;
        roundRect(ctx, left, laneTop, right - left, laneH, 12);
        ctx.fill();
        ctx.stroke();

        ctx.strokeStyle = "rgba(148,163,184,0.20)";
        ctx.beginPath();
        ctx.moveTo(left, laneTop - gap + 0.5);
        ctx.lineTo(right, laneTop - gap + 0.5);
        ctx.stroke();

        const chipY = laneTop + 3;
        const chipH = laneH - 6;

        ctx.font = `650 ${fontSize}px system-ui, -apple-system, Segoe UI, Roboto, Arial`;
        ctx.textBaseline = "middle";
        ctx.textAlign = "center";

        for (const w of wordIntervals) {
          const startC = clipMin == null ? w.start : Math.max(w.start, clipMin);
          const endC = clipMax == null ? w.end : Math.min(w.end, clipMax);
          if (!(endC > startC)) continue;

          const x1 = xScale.getPixelForValue(startC);
          const x2 = xScale.getPixelForValue(endC);

          const segLeft = Math.min(x1, x2);
          const segW = Math.max(1, Math.abs(x2 - x1));
          const segCenter = segLeft + segW / 2;

          const drawW = Math.max(minChipW, segW);
          let chipX = segLeft;
          if (segW < minChipW) chipX = segCenter - drawW / 2;

          const inset = 2;
          chipX = clamp(chipX, left + inset, right - inset - drawW);

          const isActive =
            activeWord &&
            w.start === activeWord.start &&
            w.end === activeWord.end;

          const isHovered =
            hoveredWord &&
            w.start === hoveredWord.start &&
            w.end === hoveredWord.end;

          const isActiveHovered = isActive && isHovered;

          if (isActiveHovered) {
            ctx.fillStyle = "rgba(16,185,129,0.30)";
            ctx.strokeStyle = "rgba(16,185,129,1)";
            ctx.lineWidth = 2.2;

            ctx.shadowColor = "rgba(16,185,129,0.35)";
            ctx.shadowBlur = 14;
            ctx.shadowOffsetY = 4;

            const lift = 2.5;

            ctx.save();
            ctx.shadowColor = "rgba(16,185,129,0.20)";
            ctx.shadowBlur = 18;
            roundRect(
              ctx,
              chipX - 1,
              chipY - lift - 1,
              drawW + 2,
              chipH + 2,
              WORD_LANE.radius
            );
            ctx.fillStyle = "rgba(16,185,129,0.08)";
            ctx.fill();
            ctx.restore();

            roundRect(ctx, chipX, chipY - lift, drawW, chipH, WORD_LANE.radius);
            ctx.fill();

            ctx.shadowBlur = 0;
            ctx.shadowOffsetY = 0;

            roundRect(ctx, chipX, chipY - lift, drawW, chipH, WORD_LANE.radius);
            ctx.stroke();
          } else if (isActive) {
            ctx.fillStyle = "rgba(16,185,129,0.22)";
            ctx.strokeStyle = "rgba(16,185,129,0.95)";
            ctx.lineWidth = 1.4;

            ctx.shadowColor = "rgba(0,0,0,0.10)";
            ctx.shadowBlur = 6;
            ctx.shadowOffsetY = 2;

            roundRect(ctx, chipX, chipY, drawW, chipH, WORD_LANE.radius);
            ctx.fill();

            ctx.shadowBlur = 0;
            ctx.shadowOffsetY = 0;

            roundRect(ctx, chipX, chipY, drawW, chipH, WORD_LANE.radius);
            ctx.stroke();
          } else if (isHovered) {
            ctx.fillStyle = "rgba(241,245,249,0.95)";
            ctx.strokeStyle = "rgba(59,130,246,0.75)";
            ctx.lineWidth = 1.2;

            ctx.shadowColor = "rgba(0,0,0,0.12)";
            ctx.shadowBlur = 8;
            ctx.shadowOffsetY = 2;

            roundRect(
              ctx,
              chipX,
              chipY - 0.5,
              drawW,
              chipH + 1,
              WORD_LANE.radius
            );
            ctx.fill();

            ctx.shadowBlur = 0;
            ctx.shadowOffsetY = 0;

            roundRect(
              ctx,
              chipX,
              chipY - 0.5,
              drawW,
              chipH + 1,
              WORD_LANE.radius
            );
            ctx.stroke();
          } else {
            ctx.fillStyle = "rgba(241,245,249,0.70)";
            ctx.strokeStyle = "rgba(148,163,184,0.55)";
            ctx.lineWidth = 1;

            roundRect(ctx, chipX, chipY, drawW, chipH, WORD_LANE.radius);
            ctx.fill();
            ctx.stroke();
          }

          if (showWordLabels) {
            const available = drawW - padX * 2;
            let text = w.text ?? "";

            if (ctx.measureText(text).width > available) {
              while (
                text.length > 1 &&
                ctx.measureText(text + "…").width > available
              ) {
                text = text.slice(0, -1);
              }
              if (text !== (w.text ?? "")) text += "…";
            }

            ctx.fillStyle = isActiveHovered
              ? "rgba(5,150,105,1)"
              : isActive
              ? "rgba(5,150,105,1)"
              : isHovered
              ? "rgba(37,99,235,0.95)"
              : "rgba(51,65,85,0.90)";

            ctx.fillText(text, chipX + drawW / 2, chipY + chipH / 2);
          }
        }
      }

      if (cursorX != null) {
        const xC = xScale.getPixelForValue(cursorX);
        if (xC >= left && xC <= right) {
          ctx.strokeStyle = "rgba(255,255,255,0.90)";
          ctx.lineWidth = isMobile ? 3 : 3.5;
          ctx.beginPath();
          ctx.moveTo(xC, top);
          ctx.lineTo(xC, effectiveLaneBottom);
          ctx.stroke();

          ctx.strokeStyle = "rgba(16,185,129,1)";
          ctx.lineWidth = isMobile ? 1.6 : 1.8;
          ctx.beginPath();
          ctx.moveTo(xC, top);
          ctx.lineTo(xC, effectiveLaneBottom);
          ctx.stroke();
        }
      }

      if (
        showBall &&
        cursorEnvelopePoint &&
        Number.isFinite(Number(cursorEnvelopePoint.x)) &&
        Number.isFinite(Number(cursorEnvelopePoint.y))
      ) {
        const r = ballRadius ?? (isMobile ? 8.5 : 12);
        const px = xScale.getPixelForValue(cursorEnvelopePoint.x);
        const py = clamp(
          yScale.getPixelForValue(cursorEnvelopePoint.y),
          top + r + 2,
          bottom - r - 2
        );

        if (px >= left && px <= right && py >= top - 6) {
          ctx.save();
          ctx.shadowColor = "rgba(16,185,129,0.48)";
          ctx.shadowBlur = isMobile ? 14 : 18;
          ctx.beginPath();
          ctx.arc(px, py, r + 1.25, 0, Math.PI * 2);
          ctx.fillStyle = "rgba(16,185,129,0.95)";
          ctx.fill();
          ctx.restore();

          const bodyGrad = ctx.createRadialGradient(
            px - r * 0.32,
            py - r * 0.34,
            r * 0.18,
            px,
            py,
            r
          );
          bodyGrad.addColorStop(0, "rgba(220,252,231,1)");
          bodyGrad.addColorStop(0.2, "rgba(134,239,172,1)");
          bodyGrad.addColorStop(0.5, "rgba(34,197,94,1)");
          bodyGrad.addColorStop(0.8, "rgba(16,185,129,1)");
          bodyGrad.addColorStop(1, "rgba(4,120,87,1)");

          ctx.beginPath();
          ctx.arc(px, py, r, 0, Math.PI * 2);
          ctx.fillStyle = bodyGrad;
          ctx.fill();

          ctx.lineWidth = 2.4;
          ctx.strokeStyle = "rgba(255,255,255,0.92)";
          ctx.stroke();

          ctx.beginPath();
          ctx.arc(
            px - r * 0.34,
            py - r * 0.38,
            Math.max(1.8, r * 0.26),
            0,
            Math.PI * 2
          );
          ctx.fillStyle = "rgba(255,255,255,0.88)";
          ctx.fill();

          ctx.beginPath();
          ctx.arc(
            px - r * 0.08,
            py - r * 0.14,
            Math.max(1.2, r * 0.11),
            0,
            Math.PI * 2
          );
          ctx.fillStyle = "rgba(255,255,255,0.3)";
          ctx.fill();

          ctx.beginPath();
          ctx.arc(
            px + r * 0.28,
            py + r * 0.34,
            Math.max(1.4, r * 0.18),
            0,
            Math.PI * 2
          );
          ctx.fillStyle = "rgba(0,0,0,0.10)";
          ctx.fill();
        }
      }

      ctx.restore();
    },
  };
}

export default function IntensityAnimation({
  intensity,
  segment,
  audioRef,
  speed = 1,
  showIntensity = true,
  voicedXMin: voicedXMinProp,
  voicedXMax: voicedXMaxProp,
  onWordClick,
}) {
  const [isMobile, setIsMobile] = useState(false);
  const [showWordLabels, setShowWordLabels] = useState(true);
  const [hoveredWord, setHoveredWord] = useState(null);
  const [animatedTime, setAnimatedTime] = useState(0);

  const rafRef = useRef(null);
  const displayTimeRef = useRef(0);

  const wordPlayRafRef = useRef(null);
  const wordPlayEndRef = useRef(null);

  const xs = useMemo(() => intensity?.xs || [], [intensity]);
  const ys = useMemo(() => intensity?.ys || [], [intensity]);

  const segmentDuration = useMemo(() => {
    const start = Number(segment?.start ?? 0);
    const end = Number(segment?.end ?? start);
    return Math.max(0, end - start);
  }, [segment]);

  const wordIntervals = useMemo(() => {
    const segStart = Number(segment?.start ?? 0);
    const segEnd = Number(segment?.end ?? segStart);
    const segDuration = Math.max(0, segEnd - segStart);

    return (
      segment?.words?.map((w) => {
        const start = clamp(Number(w.start) - segStart, 0, segDuration);
        const end = clamp(Number(w.end) - segStart, start, segDuration);

        return {
          start,
          end,
          text: String(w.text ?? ""),
        };
      }) || []
    );
  }, [segment]);

  const shouldShowWordLane = useMemo(() => {
    return wordIntervals.length > 0 && wordIntervals.length <= 15;
  }, [wordIntervals.length]);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      setShowWordLabels(!mobile && wordIntervals.length <= 15);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, [wordIntervals.length]);

  const stopWordPlayback = useCallback(() => {
    if (wordPlayRafRef.current) {
      cancelAnimationFrame(wordPlayRafRef.current);
      wordPlayRafRef.current = null;
    }
    wordPlayEndRef.current = null;
  }, []);

  const playWordRangePrecise = useCallback(
    (relStart, relEnd) => {
      const a = audioRef?.current;
      if (!a || !segment) return;

      const segStart = Number(segment.start ?? 0);
      const segEnd = Number(segment.end ?? segStart);
      const segDuration = Math.max(0, segEnd - segStart);

      const END_GUARD = 0.05;
      const STOP_EARLY = 0.012;

      const safeRelStart = clamp(Number(relStart), 0, segDuration);
      const maxRelEnd = Math.max(
        safeRelStart,
        Math.max(0, segDuration - END_GUARD)
      );
      const safeRelEnd = clamp(Number(relEnd), safeRelStart, maxRelEnd);

      const absStart = segStart + safeRelStart;
      const absEnd = segStart + safeRelEnd;

      stopWordPlayback();

      a.pause();
      a.currentTime = absStart;
      wordPlayEndRef.current = absEnd;

      const playPromise = a.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {});
      }

      const stopNow = () => {
        const aa = audioRef?.current;
        if (!aa) return;
        aa.pause();
        aa.currentTime = Math.min(absEnd - 0.001, segEnd - END_GUARD);
        stopWordPlayback();
      };

      const tick = () => {
        const aa = audioRef?.current;
        const targetEnd = wordPlayEndRef.current;

        if (!aa || targetEnd == null) return;

        if (aa.currentTime >= targetEnd - STOP_EARLY) {
          stopNow();
          return;
        }

        wordPlayRafRef.current = requestAnimationFrame(tick);
      };

      wordPlayRafRef.current = requestAnimationFrame(tick);
    },
    [audioRef, segment, stopWordPlayback]
  );

  useEffect(() => {
    const audio = audioRef?.current;
    if (!audio || segment?.start == null) {
      displayTimeRef.current = 0;
      setAnimatedTime(0);
      return;
    }

    const syncNow = () => {
      const t = Math.max(0, audio.currentTime - Number(segment.start));
      displayTimeRef.current = t;
      setAnimatedTime(t);
    };

    const stop = () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };

    const tick = () => {
      const target = Math.max(0, audio.currentTime - Number(segment.start));
      const prev = displayTimeRef.current ?? target;
      const smoothed = prev + (target - prev) * 0.5;

      displayTimeRef.current = smoothed;
      setAnimatedTime(smoothed);

      if (!audio.paused && !audio.ended) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        rafRef.current = null;
      }
    };

    const start = () => {
      stop();
      syncNow();
      rafRef.current = requestAnimationFrame(tick);
    };

    const handlePause = () => {
      syncNow();
      stop();
    };

    const handleSeeked = () => {
      syncNow();
      if (!audio.paused && !audio.ended && !rafRef.current) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };

    syncNow();

    audio.addEventListener("play", start);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("ended", handlePause);
    audio.addEventListener("seeked", handleSeeked);
    audio.addEventListener("loadedmetadata", syncNow);
    audio.addEventListener("ratechange", handleSeeked);

    if (!audio.paused && !audio.ended) {
      rafRef.current = requestAnimationFrame(tick);
    }

    return () => {
      stop();
      audio.removeEventListener("play", start);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("ended", handlePause);
      audio.removeEventListener("seeked", handleSeeked);
      audio.removeEventListener("loadedmetadata", syncNow);
      audio.removeEventListener("ratechange", handleSeeked);
    };
  }, [audioRef, segment?.start]);

  useEffect(() => {
    return () => stopWordPlayback();
  }, [stopWordPlayback]);

  useEffect(() => {
    stopWordPlayback();
  }, [segment, stopWordPlayback]);

  const hasData = xs.length > 0 && ys.length > 0;

  const voicedXMin = useMemo(() => {
    if (Number.isFinite(Number(voicedXMinProp))) return Number(voicedXMinProp);
    return xs.length ? xs[0] : 0;
  }, [voicedXMinProp, xs]);

  const voicedXMax = useMemo(() => {
    if (Number.isFinite(Number(voicedXMaxProp))) return Number(voicedXMaxProp);
    return xs.length ? xs[xs.length - 1] : 1;
  }, [voicedXMaxProp, xs]);

  const cursorX = useMemo(() => {
    if (!xs.length) return null;
    const t = Number(animatedTime);
    const raw = isFiniteNum(t) ? t : voicedXMin;
    return clamp(raw, voicedXMin, voicedXMax);
  }, [animatedTime, xs.length, voicedXMin, voicedXMax]);

  const showBall = true;

  const guideCurve = useMemo(() => {
    const numericYs = ys.map((v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    });
    return buildContinuousGuideCurve(xs, numericYs);
  }, [xs, ys]);

  const predictedCursorX = useMemo(() => {
    if (!xs.length) return null;

    const leadSeconds =
      speed <= 0.25 ? 0.01 : speed <= 0.5 ? 0.008 : 0.005;

    return clamp((cursorX ?? voicedXMin) + leadSeconds, voicedXMin, voicedXMax);
  }, [cursorX, xs.length, voicedXMin, voicedXMax, speed]);

  const { yMin, yMax } = useMemo(() => {
    if (!ys.length) return { yMin: 0, yMax: 1 };
    let mn = Infinity;
    let mx = -Infinity;
    for (let i = 0; i < ys.length; i++) {
      const v = Number(ys[i]);
      if (!Number.isFinite(v)) continue;
      mn = Math.min(mn, v);
      mx = Math.max(mx, v);
    }
    if (!Number.isFinite(mn) || !Number.isFinite(mx))
      return { yMin: 0, yMax: 1 };
    return { yMin: mn, yMax: mx };
  }, [ys]);

  const yRange = useMemo(() => Math.max(1e-6, yMax - yMin), [yMax, yMin]);

  const cursorEnvelopePoint = useMemo(() => {
    if (!guideCurve.length || predictedCursorX == null) return null;

    const p = interpolateOnCurve(guideCurve, predictedCursorX);
    if (!p) return null;

    const normalizedHeight = yRange > 0 ? (p.y - yMin) / yRange : 0;
    const baseLift = Math.max(8, yRange * 0.065);
    const peakLift =
      Math.max(0, normalizedHeight) * Math.max(2.2, yRange * 0.03);

    return {
      x: p.x,
      y: Math.max(0, p.y + baseLift + peakLift),
    };
  }, [guideCurve, predictedCursorX, yMin, yRange]);

  const ballRadius = useMemo(() => {
    return isMobile ? 8.5 : 12;
  }, [isMobile]);

  const activeWord = useMemo(() => {
    if (!shouldShowWordLane || cursorX == null) return null;
    const EPS = 0.0005;

    for (let i = 0; i < wordIntervals.length; i++) {
      const w = wordIntervals[i];
      const isLast = i === wordIntervals.length - 1;

      if (
        (isLast && cursorX >= w.start - EPS && cursorX <= w.end + EPS) ||
        (!isLast && cursorX >= w.start - EPS && cursorX < w.end - EPS)
      ) {
        return w;
      }
    }

    return null;
  }, [cursorX, wordIntervals, shouldShowWordLane]);

  const shouldShowWordLabels = useMemo(() => {
    return showWordLabels && shouldShowWordLane;
  }, [showWordLabels, shouldShowWordLane]);

  const lanePxGetter = useMemo(() => {
    return (mobile) =>
      (mobile ? WORD_LANE.heightMobile : WORD_LANE.heightDesktop) +
      WORD_LANE.gapFromPlot;
  }, []);

  const lanePlugin = useMemo(() => makeLanePlugin(lanePxGetter), [lanePxGetter]);

  const data = useMemo(() => {
    const datasets = [];

    if (showIntensity) {
      datasets.push({
        label: "Intensity (dB)",
        data: xs.map((x, i) => ({ x, y: ys[i] })),
        borderColor: "#2563eb",
        borderWidth: isMobile ? 1.75 : 2.25,
        tension: isMobile ? 0.25 : 0.42,
        cubicInterpolationMode: "monotone",
        pointRadius: 0,
        pointHoverRadius: 0,
        pointHitRadius: 0,
        showLine: true,
        datalabels: { display: false },
      });
    }

    return { datasets };
  }, [xs, ys, showIntensity, isMobile]);

  const pickWordAtEvent = useCallback(
    (event, chart) => {
      if (!shouldShowWordLane) return null;
      if (!chart) return null;

      const { chartArea, scales } = chart;
      const xScale = scales?.x;
      if (!xScale || !chartArea) return null;

      const pos = getRelativePosition(event, chart);
      const xPx = pos.x;
      const yPx = pos.y;

      const laneH = isMobile ? WORD_LANE.heightMobile : WORD_LANE.heightDesktop;
      const laneTop = chartArea.bottom - laneH;
      const laneBottom = chartArea.bottom;

      if (xPx < chartArea.left || xPx > chartArea.right) return null;
      if (yPx < laneTop || yPx > laneBottom) return null;

      for (const w of wordIntervals) {
        const bounds = getRenderedWordChipBounds({
          word: w,
          xScale,
          chartArea,
          isMobile,
          clipMin: voicedXMin,
          clipMax: voicedXMax,
        });

        if (!bounds) continue;

        if (xPx >= bounds.left && xPx <= bounds.right) {
          return w;
        }
      }

      const t = xScale.getValueForPixel(xPx);
      if (!Number.isFinite(t)) return null;

      const EPS = 0.0005;

      for (let i = 0; i < wordIntervals.length; i++) {
        const w = wordIntervals[i];
        const isLast = i === wordIntervals.length - 1;

        if (
          (isLast && t >= w.start - EPS && t <= w.end + EPS) ||
          (!isLast && t >= w.start - EPS && t < w.end - EPS)
        ) {
          return w;
        }
      }

      return null;
    },
    [
      isMobile,
      wordIntervals,
      shouldShowWordLane,
      voicedXMin,
      voicedXMax,
    ]
  );

  const options = useMemo(() => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      layout: { padding: { left: 8, right: 12, top: 8, bottom: 8 } },

      onHover: (event, _elements, chart) => {
        const w = pickWordAtEvent(event, chart);
        setHoveredWord(w);
        if (chart?.canvas) {
          chart.canvas.style.cursor = w ? "pointer" : "default";
        }
      },

      onClick: (event, _elements, chart) => {
        const w = pickWordAtEvent(event, chart);
        if (!w) return;

        playWordRangePrecise(w.start, w.end);

        if (typeof onWordClick === "function") {
          onWordClick(w);
        }
      },

      scales: {
        x: {
          type: "linear",
          min: voicedXMin,
          max: voicedXMax,
          grid: { drawBorder: false, color: "rgba(148,163,184,0.14)" },
          ticks: { display: false },
          title: { display: false },
        },
        y: {
          min: yMin - yRange * 0.08,
          max: yMax + yRange * 0.08,
          grid: { drawBorder: false, color: "rgba(148,163,184,0.14)" },
          title: {
            display: true,
            text: "Intensity (dB)",
            color: "rgba(51,65,85,0.80)",
            font: { size: isMobile ? 12 : 13, weight: "600" },
          },
          ticks: {
            color: "rgba(51,65,85,0.70)",
            font: { size: isMobile ? 10 : 11 },
            maxTicksLimit: 5,
          },
        },
      },

      plugins: {
        legend: { display: false },
        tooltip: { enabled: false },
        datalabels: { display: false },
        lanePlugin: {
          wordIntervals,
          activeWord,
          hoveredWord,
          cursorX,
          cursorEnvelopePoint,
          ballRadius,
          showBall,
          showWordLabels: shouldShowWordLabels,
          showWordLane: shouldShowWordLane,
          isMobile,
          xWindowMin: voicedXMin,
          xWindowMax: voicedXMax,
        },
      },

      interaction: { mode: undefined, axis: "x", intersect: false },
      hover: { mode: undefined },
    };
  }, [
    isMobile,
    yMin,
    yMax,
    yRange,
    wordIntervals,
    activeWord,
    hoveredWord,
    cursorX,
    cursorEnvelopePoint,
    ballRadius,
    showBall,
    shouldShowWordLabels,
    shouldShowWordLane,
    voicedXMin,
    voicedXMax,
    onWordClick,
    pickWordAtEvent,
    playWordRangePrecise,
  ]);

  if (!hasData) return null;

  return (
    <div className="flex flex-col gap-2 w-full min-w-0">
      {isMobile && shouldShowWordLane && (
        <label className="flex items-center gap-2 select-none text-xs">
          <input
            type="checkbox"
            checked={showWordLabels}
            onChange={() => setShowWordLabels((v) => !v)}
            className="scale-90"
          />
          Show word labels
        </label>
      )}

      <div
        className={`w-full min-w-0 overflow-x-auto bg-white rounded-lg border border-gray-200 p-2 ${
          isMobile ? "h-48" : "h-64"
        }`}
        onMouseLeave={() => setHoveredWord(null)}
      >
        <Line data={data} options={options} plugins={[lanePlugin]} />
      </div>

      {isMobile && (
        <p className="text-xs text-gray-500 text-center">
          Tap words in the lane to replay • Labels:{" "}
          {shouldShowWordLane ? (showWordLabels ? "ON" : "OFF") : "HIDDEN"}
        </p>
      )}
    </div>
  );
}